import React from 'react';
import { connect } from 'react-redux';
import { Route, RouteComponentProps } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';

import { fetchSubscriptionPlans, getManagementIssuedOrder } from '../../actions/billing';
import { setMenuTab } from '../../actions/ui';
import AsyncLoad from '../../components/common/AsyncLoad';
import {
  ADYEN_CHECKOUT_CALLBACK_PATH,
  BILLING_INFO_DETAILS_PATH,
  BILLING_PATH,
  BILLING_PAYMENT_PATH,
  ORDER_DETAILS,
  ORDER_HISTORY,
  PAYPAL_EXECUTED_PATH,
  SidebarTab,
  USER_BILLING_INFO_PATH,
  WIRE_TRANSFER_PAYMENT_PATH,
} from '../../constants/paths/internal';
import { ThunkDispatch } from '../../meta/types/actions';
import { Order } from '../../meta/types/billing/order';
import { SalesPackage } from '../../meta/types/billing/salespackage';
import { StoreState } from '../../meta/types/store';
import BillingPageHeader from './BillingPageHeader';
import BillingStyle from './style';

type PropsFromState = {
  userId: string;
  fetchingSubscriptionPlans: boolean;
  fetchingManagementIssuedOrder: boolean;
};

type PropsFromDispatch = {
  setMenuTab: (menuTab: SidebarTab) => void;
  fetchSalesPackages: () => Promise<SalesPackage[]>;
  getManagementIssuedOrder: (userId: string) => Promise<Order | undefined>;
};

type BillingPageProps = PropsFromState & PropsFromDispatch & RouteComponentProps;

class BillingPage extends React.Component<BillingPageProps> {
  componentDidMount() {
    this.props.setMenuTab(SidebarTab.BILLING);
    this.props.fetchSalesPackages();
    this.props.getManagementIssuedOrder(this.props.userId);
  }

  render() {
    const { location, fetchingSubscriptionPlans, fetchingManagementIssuedOrder } = this.props;
    const isOnUserBillingInfoRoute = location.pathname === USER_BILLING_INFO_PATH;

    if (fetchingSubscriptionPlans || fetchingManagementIssuedOrder) {
      return (
        <Loader active inline="centered">
          Loading
        </Loader>
      );
    }

    return (
      <BillingStyle className="billing">
        <div className="billing__content">
          <BillingPageHeader
            isOnUserBillingInfoRoute={isOnUserBillingInfoRoute}
            pathname={this.props.history.location.pathname}
          />

          <Route
            path={WIRE_TRANSFER_PAYMENT_PATH}
            exact
            component={AsyncWireTransferPaymentCompleted}
          />
          <Route path={BILLING_PATH} exact component={AsyncBillingFlow} />
          <Route path={ORDER_HISTORY} exact component={AsyncOrderHistory} />
          <Route path={ORDER_DETAILS} exact component={AsyncOrderDetails} />
          <Route path={USER_BILLING_INFO_PATH} exact component={AsyncUserBillingInfo} />
          <Route path={PAYPAL_EXECUTED_PATH} exact component={AsyncPaypalExecutedMessage} />
          <Route path={`${USER_BILLING_INFO_PATH}/:id`} exact component={AsyncUserBillingInfo} />
          <Route path={ADYEN_CHECKOUT_CALLBACK_PATH} exact component={AsyncAdyenCheckoutCallback} />
          <Route path={BILLING_INFO_DETAILS_PATH} exact component={AsyncBillingInfoDetailsForm} />
          <Route path={BILLING_PAYMENT_PATH} exact component={AsyncPayment} />
        </div>
      </BillingStyle>
    );
  }
}

const AsyncPayment = AsyncLoad({
  loader: () => import('../../modules/billing/payment'),
});

const AsyncBillingInfoDetailsForm = AsyncLoad({
  loader: () => import('../../modules/billing/billing-info/BillingInfoDetailsForm'),
});

const AsyncAdyenCheckoutCallback = AsyncLoad({
  loader: () => import('../../modules/billing/payment/adyen/SecureCheckoutCallback'),
});

const AsyncOrderHistory = AsyncLoad({
  loader: () => import('../../modules/OrderHistory'),
});

const AsyncBillingFlow = AsyncLoad({
  loader: () => import('../../modules/billing'),
});

const AsyncOrderDetails = AsyncLoad({
  loader: () => import('../../modules/OrderDetails'),
});

const AsyncWireTransferPaymentCompleted = AsyncLoad({
  loader: () => import('../../modules/WireTransferPaymentCompleted'),
});

const AsyncUserBillingInfo = AsyncLoad({
  loader: () => import('../../modules/billing/user-billing-info'),
});

const AsyncPaypalExecutedMessage = AsyncLoad({
  loader: () => import('../../modules/PaypalExecutedMessage'),
});

const mapDispatchToProps = (dispatch: ThunkDispatch): PropsFromDispatch => {
  return {
    setMenuTab: (tab: SidebarTab) => dispatch(setMenuTab(tab)),
    fetchSalesPackages: () => dispatch(fetchSubscriptionPlans()),
    getManagementIssuedOrder: (userId: string) => dispatch(getManagementIssuedOrder(userId)),
  };
};

function mapStateToProps(state: StoreState): PropsFromState {
  return {
    userId: state.auth.configurableUser!.id,
    fetchingSubscriptionPlans: state.billing.fetchingSubscriptionPlans,
    fetchingManagementIssuedOrder: state.billing.fetchingManagementIssuedOrder,
  };
}

const enhance = connect(mapStateToProps, mapDispatchToProps);

export default enhance(BillingPage);
